import { Ionicons, MaterialIcons } from "@expo/vector-icons";
import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { Link, router, useFocusEffect } from "expo-router";
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { View, Text, ScrollView, Pressable, FlatList } from "react-native";
import { CheckBox } from "react-native-btr";
import Emoji from "react-native-emoji";
import { useDispatch } from "react-redux";

import Subtitle from "@/components/Subtitle";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import ButtonBottomBorder from "@/components/button/ButtonBottomBorder";
import Input from "@/components/input/Input";
import InputAmount from "@/components/input/InputAmount";
import InputChoose from "@/components/input/InputChoose";
import InputCryptoAmount from "@/components/input/InputCryptoAmount";
import ReviewModal from "@/components/modal/banking/ReviewModal";
import CreateWalletAddress from "@/components/web3/CreateWalletAddress";
import { WidgetInformation } from "@/components/web3/WalletWidget";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { sendRequest } from "@/utils/ApiUtil";
import {
  getAccountsWithSameAsset,
  getAmountFormattedBaseOnAssetType,
} from "@/utils/amount/AmountUtils";
import { getIsBridgeActiveFromCookies } from "@/utils/cookies/Cookies";
import { WithdrawalRequest } from "@/utils/dto/accounts.request";
import { WithdrawalPurpose } from "@/utils/dto/extracted_enums";
import { checkAsset } from "@/utils/transactions/ExchangeCurrencies";

export interface Web3Transfer extends WidgetInformation {
  destinationAddress: string;
  rail: string;
  currency: string;
  fee: number;
  memo?: string;
}

const initialWeb3TransferData: Web3Transfer = {
  walletAddress: "",
  destinationAddress: "",
  rail: "",
  currency: "",
  memo: "",
  isTronWallet: false,
  fee: 0,
};

interface RequestData {
  withdrawalRail: any;
  sourceAccountId: any;
  purpose: string;
  amount: number;
  destinationCounterpartyId: any;
  description?: string;
  memo?: string;
  scheduled?: boolean;
}

type FormData = {
  amount: string;
  purpose: string;
  description: string;
  memo: string;
};

export default function Amount() {
  const [sourceAccountId, setSourceAccountId] = useState("");
  const [purpose, setPurpose] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [amountTyped, setAmountTyped] = useState(0);
  const [amountAvailable, setAmountAvailable] = useState(0);
  const [currentAccountData, setCurrentAccountData] = useState<any>({});
  const [resetAmountTyped, setResetAmountTyped] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [isAccountsListVisible, setIsAccountsListVisible] = useState(false);
  const [isUsingWallet, setIsUsingWallet] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);
  const [isSubmittingWallet, setIsSubmittingWallet] = useState(false);
  const [isInputCryptoEditable, setIsInputCryptoEditable] = useState(true);
  const [isAbleToUseWallet, setIsAbleToUseWallet] = useState(false);
  const [errorToSubmit, setErrorToSubmit] = useState(true);
  //State for CreateWalletAddress
  const [web3TransferData, setWeb3TransferData] = useState<Web3Transfer>(
    initialWeb3TransferData
  );
  const WIRE_LIMITED_AMOUNT = 501000;

  const resolver = classValidatorResolver(WithdrawalRequest, {
    validator: { skipMissingProperties: true },
  });

  const { control, handleSubmit, formState, reset, setValue } =
    useForm<FormData>({
      mode: "onSubmit",
      resolver,
    });

  const { isSubmitting } = formState;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const globalValues = useAppSelector((state: any) => state);

  const currentAccountId = globalValues?.currentAccountId;
  const currentCounterparty = globalValues.currentCounterpartySelected;

  const resetStates = () => {
    reset();
    setWeb3TransferData(initialWeb3TransferData);
    setErrorMessage("");
    setPurpose("");
    setAmountTyped(0);
    setErrorToSubmit(true);
    setIsInputCryptoEditable(true);
    setIsUsingWallet(false);
    setIsScheduled(false);
    setIsSubmittingWallet(false);
    setIsAccountsListVisible(false);
  };

  const checkIsAbleToUseWallet = () => {
    if (globalValues.rail === "SWIFT") {
      return false;
    }
    return getIsBridgeActiveFromCookies() === "ACTIVE";
  };

  useFocusEffect(
    React.useCallback(() => {
      if (currentCounterparty) {
        setValue("memo", currentCounterparty?.profile?.name);
      }
      dispatch(setForm({ currentTransactionId: "" }));
      setIsAbleToUseWallet(checkIsAbleToUseWallet());
      return () => {
        resetStates();
      };
    }, [currentCounterparty])
  );

  useFocusEffect(
    React.useCallback(() => {
      const accountsData = globalValues.accountsData;

      if (accountsData && currentAccountId) {
        const currentAccountSelected = accountsData.find(
          (account: any) => account.id === currentAccountId
        );
        const accountWithSameCounterpartyAsset = accountsData.find(
          (account: any) =>
            checkAsset(account?.assetTypeId) ===
            checkAsset(
              currentCounterparty?.walletInformation?.assetTypeId ||
                currentCounterparty?.accountInformation?.assetTypeId
            )
        );
        const accountFiatUsd = accountsData.find(
          (account: any) => checkAsset(account.assetTypeId) === "USD"
        );

        if (
          (globalValues.rail === "SWIFT" || globalValues.rail === "FEDWIRE") &&
          checkAsset(currentAccountSelected?.assetTypeId) !== "USD"
        ) {
          setSourceAccountId(accountFiatUsd.id);
          setAmountAvailable(accountFiatUsd.availableBalance);
          setCurrentAccountData(accountFiatUsd);
        } else if (
          globalValues.rail === "CRYPTO" &&
          checkAsset(currentAccountSelected?.assetTypeId) !==
            checkAsset(
              currentCounterparty?.walletInformation?.assetTypeId ||
                currentCounterparty?.accountInformation?.assetTypeId
            )
        ) {
          if (accountWithSameCounterpartyAsset) {
            setSourceAccountId(accountWithSameCounterpartyAsset.id);
            setAmountAvailable(
              accountWithSameCounterpartyAsset.availableBalance
            );
            setCurrentAccountData(accountWithSameCounterpartyAsset);
          } else {
            setSourceAccountId("");
            setAmountAvailable(0);
            setIsInputCryptoEditable(false);
            setErrorToSubmit(true);
            setErrorMessage(t("common:error.noaccountwithasset"));
          }
        } else {
          setSourceAccountId(currentAccountId);
          setAmountAvailable(currentAccountSelected.availableBalance);
          setCurrentAccountData(currentAccountSelected);
        }
      }
    }, [
      globalValues.accountsData,
      globalValues.rail,
      currentCounterparty,
      currentAccountId,
    ])
  );

  useEffect(() => {
    if (
      amountTyped === 0 ||
      amountTyped === null ||
      amountTyped === undefined
    ) {
      setErrorMessage("");
      setErrorToSubmit(true);
    } else if (amountTyped > WIRE_LIMITED_AMOUNT && !isUsingWallet) {
      setErrorMessage(t("common:error.amountgreaterthan500k"));
      setErrorToSubmit(true);
    } else if (amountTyped > amountAvailable && !isUsingWallet) {
      setIsScheduled(true);
      if (isScheduled && purpose !== "") setErrorToSubmit(false);
    } else if (
      amountTyped <= amountAvailable &&
      purpose === "" &&
      !isUsingWallet
    ) {
      setErrorMessage("");
      setErrorToSubmit(true);
    } else if (amountTyped <= amountAvailable && purpose !== "") {
      setErrorMessage("");
      setErrorToSubmit(false);
    } else if (
      amountTyped > amountAvailable &&
      !isScheduled &&
      !isUsingWallet
    ) {
      setErrorMessage(t("common:error.amountgreater"));
      setErrorToSubmit(true);
    } else if (
      isUsingWallet &&
      web3TransferData.rail === "" &&
      web3TransferData.currency === "" &&
      amountTyped <= 0
    ) {
      setErrorToSubmit(true);
    } else if (
      amountTyped > 0 &&
      isUsingWallet &&
      web3TransferData.rail !== "" &&
      web3TransferData.currency !== ""
    ) {
      setErrorToSubmit(false);
    }
  }, [amountTyped, purpose, isScheduled, web3TransferData, isUsingWallet]);

  const onSubmitWithWallet = async (
    walletInformation: Web3Transfer,
    counterpartyId: string
  ) => {
    setIsSubmittingWallet(true);
    try {
      const request = await sendRequest(
        "get",
        "/bridge/liquidation-address",
        {
          payment_rail: walletInformation.rail,
          currency: walletInformation.currency,
          counterpartyId,
        },
        true
      );
      if (request) {
        setWeb3TransferData((prev) => ({
          ...prev,
          destinationAddress: request.address,
          memo: request.destinationBlockchainMemo ?? "",
          fee: 0,
        }));
        setShowReviewModal(true);
      }
    } catch (error: any) {
      console.log(error);
    } finally {
      setIsSubmittingWallet(false);
    }
  };

  const onSubmit = async (data: FormData) => {
    const requestData: RequestData = {
      withdrawalRail: globalValues.rail,
      sourceAccountId,
      purpose: data.purpose,
      amount: amountTyped,
      destinationCounterpartyId: currentCounterparty?.id,
    };
    if (data.description) {
      requestData.description = data.description;
    }
    if (data.memo) {
      requestData.memo = data.memo;
    }
    if (isScheduled) {
      requestData.scheduled = true;
    }
    try {
      const request = await sendRequest(
        "POST",
        "/withdrawal",
        requestData,
        true
      );
      if (request && !errorToSubmit) {
        if (!isScheduled) {
          dispatch(setForm({ withdrawalId: request.id }));
          dispatch(setForm({ amountToSend: amountTyped }));
          setShowReviewModal(true);
        } else {
          dispatch(setForm({ transactionResponse: "SCHEDULED" }));
          dispatch(setForm({ transactionModalVisible: true }));
          router.push("/home");
        }
      }
    } catch (error: any) {
      console.log(error);
    }
  };

  const renderItem = (item: any) => {
    return (
      <Pressable
        onPress={() => {
          dispatch(setForm({ currentAccountId: item.id }));
          setSourceAccountId(item.id);
          setAmountTyped(0);
          setResetAmountTyped(true);
          setIsAccountsListVisible(false);
        }}
        className="flex-row bg-white1 hover:bg-gray items-center justify-center w-full h-fit p-1 border-b border-gray"
      >
        <View className="items-center">
          <Text className="text-sm mx-1">
            {`${getAmountFormattedBaseOnAssetType(
              item.availableBalance,
              item.assetTypeId,
              globalValues.rail
            )}`}
          </Text>
        </View>
      </Pressable>
    );
  };

  return (
    <ScrollView contentContainerClassName="flex-grow items-center bg-white1 p-4 flex-col lg:p-0">
      <Link href="/home/transaction/contacts" asChild>
        <View className="absolute top-0 left-0 m-2 z-50">
          <Ionicons name="chevron-back-outline" size={32} color="#BDBDBD" />
        </View>
      </Link>
      <Link href="/home" asChild>
        <View className="absolute top-0 right-0 m-2 z-50">
          <Ionicons name="close-outline" size={32} color="#BDBDBD" />
        </View>
      </Link>
      <View className="m-2">
        <View className="flex-row w-full items-center justify-center">
          <View className="m-2">
            <Title text={`${t("home/amount:title")}`} />
          </View>
          <Emoji name="money_with_wings" style={{ fontSize: 20 }} />
        </View>
        <Subtitle text={t("home/amount:subtitle")} />
      </View>
      <View className="flex-grow w-full items-center justify-center z-50">
        <View className="z-50 flex-col w-full items-center">
          <View className="flex-row items-center">
            <View className="mr-2">
              <Text className="font-semibold">
                {globalValues.rail !== "CRYPTO" ? "$" : ""}
              </Text>
            </View>
            <View>
              {globalValues.rail === "CRYPTO" || isUsingWallet ? (
                <InputCryptoAmount
                  name="amount"
                  control={control}
                  handleChange={(value) => setAmountTyped(value)}
                  handleReset={(value) => setResetAmountTyped(value)}
                  availableBalance={amountAvailable}
                  placeholder="0.00"
                  resetValue={resetAmountTyped}
                  editable={isInputCryptoEditable}
                  showPercentages={isUsingWallet}
                />
              ) : (
                <InputAmount
                  name="amount"
                  control={control}
                  type="number"
                  handleChange={(value) => setAmountTyped(value)}
                  handleReset={(value) => setResetAmountTyped(value)}
                  resetValue={resetAmountTyped}
                />
              )}
            </View>
          </View>
          {!isUsingWallet && (
            <View
              className={`z-[60] border border-midgray rounded-full py-1 px-2 items-center mt-2 bg-white1 hover:bg-gray ${
                isAccountsListVisible
                  ? "rounded-b-none border-b-0 rounded-tr-3xl rounded-tl-3xl"
                  : "rounded-full"
              }`}
            >
              <Pressable
                className="flex-row items-center"
                onPress={() => setIsAccountsListVisible(!isAccountsListVisible)}
                disabled={isUsingWallet}
              >
                <Text className="text-sm font-semibold text-green">
                  {`${t(
                    "home/amount:input.availablebalance"
                  )}: ${getAmountFormattedBaseOnAssetType(
                    amountAvailable,
                    currentAccountData?.assetTypeId ?? "USD",
                    globalValues.rail
                  )}`}
                </Text>
                <MaterialIcons
                  name="keyboard-arrow-down"
                  size={18}
                  color="#919191"
                />
              </Pressable>
              {isAccountsListVisible && (
                <FlatList
                  data={getAccountsWithSameAsset(
                    globalValues.accountsData,
                    currentCounterparty,
                    globalValues.rail
                  )}
                  renderItem={(item) => renderItem(item.item)}
                  className={`z-50 absolute top-7 w-full max-h-32 bg-white1 border border-midgray rounded-bl-3xl ${
                    getAccountsWithSameAsset(
                      globalValues.accountsData,
                      currentCounterparty,
                      globalValues.rail
                    ).length <= 3
                      ? "rounded-br-3xl"
                      : ""
                  }`}
                />
              )}
            </View>
          )}
          <View style={{ height: 20 }}>
            <View className="items-center m-1">
              <Text className="text-xs text-red">{errorMessage}</Text>
            </View>
          </View>
          <View className="flex-row w-full items-center justify-center my-2 gap-6">
            <ButtonBottomBorder
              label={t("home/amount:sendwithwallet.options.sendwithizzi")}
              isSelected={!isUsingWallet}
              onClick={() => {
                resetStates();
                setIsUsingWallet(false);
              }}
            />
            <ButtonBottomBorder
              label={t("home/amount:sendwithwallet.options.sendwithwallet")}
              isSelected={isUsingWallet}
              disabled={!isAbleToUseWallet}
              onClick={() => {
                resetStates();
                setIsUsingWallet(true);
              }}
            />
          </View>
          {!isUsingWallet ? (
            <>
              <View className="mt-2 z-50">
                <InputChoose
                  label={t("home/amount:input.purpose")}
                  name="purpose"
                  data={{ WithdrawalPurpose }}
                  control={control}
                  handleChange={(value) => setPurpose(value)}
                />
              </View>
              {globalValues.rail !== "CRYPTO" && (
                <Input
                  label={t("home/amount:input.memo")}
                  control={control}
                  name="memo"
                  withCustomLabel={t("common:terms.memo")}
                />
              )}
              <Input
                label={t("home/amount:input.description")}
                control={control}
                name="description"
                optional
              />
            </>
          ) : (
            <View className="mt-4 z-50">
              <CreateWalletAddress
                setWalletInformation={(walletInformation) => {
                  setWeb3TransferData((prev) => ({
                    ...prev,
                    rail: walletInformation.rail,
                    currency: walletInformation.currency,
                    walletAddress: walletInformation.walletAddress,
                    isTronWallet: walletInformation.isTronWallet,
                  }));
                }}
                reset={isUsingWallet}
              />
            </View>
          )}
        </View>
      </View>
      {!isUsingWallet && (
        <View className="flex-row w-full items-center justify-center gap-x-2">
          <CheckBox
            checked={isScheduled}
            onPress={() => setIsScheduled(!isScheduled)}
            color="#5D0CBA"
          />
          <Text className="text-base font-light">
            {t("home/amount:schedule")}
          </Text>
        </View>
      )}
      <Button
        label={
          isScheduled ? t("common:button.confirm") : t("common:button.review")
        }
        blocked={errorToSubmit}
        isLoading={isSubmitting || isSubmittingWallet}
        {...(isUsingWallet && {
          onClick: () =>
            onSubmitWithWallet(web3TransferData, currentCounterparty?.id),
        })}
        {...(!isUsingWallet && {
          onClick: handleSubmit(onSubmit),
        })}
      />
      <ReviewModal
        visible={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        amountToSend={amountTyped}
        rail={globalValues.rail}
        isUsingWallet={isUsingWallet}
        web3TransferData={web3TransferData}
      />
    </ScrollView>
  );
}
